import { makeObservable, observable, action } from 'mobx';
import UsersRequests from 'Utils/Requests/UsersRequests';
import React from 'react';
import AppStore from "Stores/AppStore";
import IntegrationSettingsStore from 'Stores/integrationSettingsStore';
import SettingsRequests from 'Utils/Requests/SettingsRequests';
import { ShowAlert, ShowModal, CloseModal, SynchronizingStatus, SynchronizingTask } from "Utils/CommonFunctions";
import DeviceDbModal from 'Components/UsersView/deviceDbModal';
import RemoveUserModal from "Components/UsersView/removeUserModal";
import TracesStore from 'Stores/tracesStore';
import Helper from "Utils/Helper";
import DictionaryCustom from 'Stores/Extensions/DictionaryCustom';
import { WebErrorCodeEnum } from 'Utils/Requests/WebEnums';
import SmrTableStore from 'Stores/smrTableStore';
import TasksStore from 'Stores/tasksStore';
import LoadingIndicator from 'Components/Common/LoadingIndicator';

class UsersStore {
    /** @type {SmrTableStore<WebUser>} */
    tableStore;
    usersLoading;
    usersRequested;
    usersRequestId;
    /** @type {Object.<string, string>} */
    listOfUsersToBeLoaded;
    /** @type {LoadProgress} */
    usersSyncTotalDocumentsCount;
    /** @type {null | () => void} */
    showSyncSyncedProcessModal;
    /** @type {null | () => void} */
    showSyncSyncedResultModal;
    /** @type {null | () => void} */
    closeSyncSyncedProcessModal;
    isShowedUserSyncProcessModal;
    usersReloadResult;
    doesStopSyncSyncedProcessModalNeedsToBeShown;
    /** @type {Object.<string, UserState>} */
    usersState;
    usersReloadState;
    usersReloadStateCurrentProcessedUserState;
    selectSyncUsersModalData;
    /** @type {DeviceDataDumpWeb[]} */
    deviceDbDumps;
    /** 
     * Key - UserLid
     * @type {Object.<string, {
     *  newDevice: {
     *   adding: boolean,
     *   code: string, 
     *   os: string
     *  },
     *  devices: Object.<string, {
     *   doesRemoving: boolean,
     *   doesLoggingStateChanging: boolean,
     *   doesForceResetChanging: boolean
     *  }>
     * }>} 
     */
    devicesInLoad;
    deviceTypes;
    usersLinesInfos;
    searchAlias;

    constructor() {
        makeObservable(this, {
            usersLoading: observable,
            usersRequested: observable,
            listOfUsersToBeLoaded: observable,
            usersSyncTotalDocumentsCount: observable,
            doesStopSyncSyncedProcessModalNeedsToBeShown: observable,
            usersState: observable,
            usersReloadState: observable,
            usersReloadStateCurrentProcessedUserState: observable,
            selectSyncUsersModalData: observable,
            deviceDbDumps: observable,
            devicesInLoad: observable,
            deviceTypes: observable,
            usersLinesInfos: observable,
            HandleRemoveUser: action,
            searchAlias: observable
        });

        this.usersLoading = false;
        this.usersRequested = false;
        this.usersRequestId = 0;
        this.isShowedUserSyncProcessModal = false;
        this.usersState = {};
        this.usersLinesInfos = new DictionaryCustom((lid) => {
            return (
                {
                    userLid: lid,
                    closed: true,
                    topPopup: false
                }
            );
        });

        this.ClearStore();
    }

    ClearStore = action(() => {
        this.usersLoading = false;
        this.usersRequested = false;
        this.usersRequestId++;
        this.usersSyncTotalDocumentsCount = { total: 0, loaded: 0 };
        this.lastFilterString = null;
        this.pageToSet = null;
        this.pushProvidersByDeviceTypes = {};
        this.deviceDbDumps = [];

        this.listOfUsersToBeLoaded = {};
        this.isShowedUserSyncProcessModal = false;
        this.doesStopSyncSyncedProcessModalNeedsToBeShown = false;
        this.usersState = {};
        this.usersReloadState = {};
        this.usersReloadStateCurrentProcessedUserState = {};

        this.ClearStoreForReload();
        this.ClearStoreSelectSyncUsersModal();
    });

    ClearStoreForReload() {
        this.deviceTypes = [];
        this.devicesInLoad = {};
        this.usersLinesInfos.Clear();
        this.searchAlias = "";
    }

    ClearStoreSelectSyncUsersModal() {
        this.selectSyncUsersModalData = {
            usersNamesByLids: {},
            selectedUsersLids: [],
            receiveDocumentsFrom: new Date(Date.now()),
            receiveDocumentsFromIsNeeded: true,
            renderStore: {}
        };
    };

    InitModalHandlers(
        showSyncSyncedProcessModal,
        showSyncSyncedResultModal,
        closeSyncSyncedProcessModal) {
        this.showSyncSyncedProcessModal = showSyncSyncedProcessModal;
        this.showSyncSyncedResultModal = showSyncSyncedResultModal;
        this.closeSyncSyncedProcessModal = closeSyncSyncedProcessModal;
    }

    BlockReasons(isLegendModal) {
        return [
            isLegendModal == false ? null : {
                name: 'active',
                iconElement: (<div className='user-not-active-block-icon-container show'><div className="user-not-active-block-icon" /></div>),
                description: 'Пользователь не активирован'
            },
            {
                name: 'removed',
                icon: 'b-user-removed',
                description: 'Пользователь удален (заблокирован) в СЭД'
            },
            {
                name: 'license',
                icon: 'b-license',
                description: 'У пользователя нет лицензии «Мобильное приложение»'
            },
            {
                name: 'faceMissing',
                icon: 'b-face-missing',
                description: 'Пользователь не ассоциирован с ДЛ'
            },
            {
                name: 'faceChanged',
                icon: 'b-face-changed',
                description: 'ДЛ, с которым ассоциирован пользователь, изменено'
            },
            AppStore.isMeeting == false ? {
                name: 'docSleep',
                icon: 'b-sleep',
                description: 'Превышен лимит бездействия пользователя'
            } : null,
            AppStore.isMeeting == true ? {
                name: 'meetingSleep',
                icon: 'b-sleep',
                description: 'Превышен лимит бездействия пользователя'
            } : null,
            AppStore.isMeeting == false ? {
                name: 'docCabinet',
                icon: 'b-cabinet',
                description: 'Пользователю не назначен кабинет'
            } : null,
            AppStore.isMeeting == true ? {
                name: 'meetingDisable',
                icon: 'b-meeting-disable',
                description: 'У пользователя нет права "Работа с событиями"'
            } : null,
            {
                name: AppStore.isMeeting == true ? 'meetingCertificateExpired' : 'docCertificateExpired',
                icon: 'cert-expired',
                description: 'Закончился срок действия сертификата ЭП'
            },
            {
                name: AppStore.isMeeting == true ? 'meetingCertificateExpireSoon' : 'docCertificateExpireSoon',
                icon: 'cert-expire-soon',
                description: 'Заканчивается срок действия сертификата ЭП'
            },
            {
                name: AppStore.isMeeting == true ? 'meetingLicenseExpired' : 'docLicenseExpired',
                icon: 'license-expired',
                description: 'Закончился срок действия лицензии КриптоПро'
            },
            {
                name: AppStore.isMeeting == true ? 'meetingLicenseExpireSoon' : 'docLicenseExpireSoon',
                icon: 'license-expire-soon',
                description: 'Заканчивается срок действия лицензии КриптоПро'
            }
        ].filter(it => it != null);
    }

    GetSyncModalTitle() {
        let state = this.usersReloadState;
        return (
            (
                (state.isMeeting == null || state.isAllProductModes)
                    ? ''
                    : (
                        (state.isMeeting == true
                            ? "Совещания"
                            : "Документооборот")
                        + ": "
                    )
            )
            +
            "Синхронизация пользователей"
        );
    }

    ShowStopSyncSyncedProcessModal = action(_ => {
        this.doesStopSyncSyncedProcessModalNeedsToBeShown = true;
    });

    CloseStopSyncSyncedProcessModal = action(_ => {
        this.doesStopSyncSyncedProcessModalNeedsToBeShown = false;
    });

    StopSyncSyncedProcess() {
        UsersRequests.StopSyncSyncedProcess();
    }

    GetButtonsSyncSyncedProcessModal() {
        var tmpButtons = [{
            className: 'blue-button ' +
                ((this.usersReloadState != null && (this.usersReloadState.isCancelled || (this.usersReloadState.currentStateOfUsersQueue != null && this.usersReloadState.currentStateOfUsersQueue.length < 2)))
                    ? "disabled" : ""),
            content: 'Прервать',
            onClick: this.ShowStopSyncSyncedProcessModal.bind(this)
        }];

        return tmpButtons;
    }

    GetInitData() {
        UsersRequests.GetInitData().then(action((values) => {
            if (values.error)
                return;

            this.deviceTypes = values.deviceTypes;
            this.pushProvidersByDeviceTypes = values.pushProvidersByDeviceTypes;
            this.deviceDbDumps = values.deviceDbDumps;

            for (let i = 0; i < values.initialStateOfUsersQueue.length; i++)
                this.listOfUsersToBeLoaded[values.initialStateOfUsersQueue[i].lid] = values.initialStateOfUsersQueue[i].userName;

            if (values.usersQueueSyncInfo.length > 0) {
                this.listOfUsersToBeLoaded = {};
                this.usersReloadResult = values.usersQueueSyncInfo;
                this.showSyncSyncedResultModal();
            } else if (values.usersReloadState.inProcess) {
                this.SetUsersReloadState(values.usersReloadState);
            }
        }));
    }

    SetTableStore(store) {
        this.tableStore = store;
    }

    CleanTableStore() {
        this.tableStore = null;
    }

    /**
     * @param {WebUser} user 
     * @param {boolean} physically
     */
    ShowRemoveUserModal(user, physically) {
        ShowModal({
            canBeClosed: true,
            header: 'Удаление пользователя',
            className: 'remove-user-modal',
            children: <RemoveUserModal
                user={user}
                physically={physically} />,
            buttons: [{
                closer: true,
                content: 'Нет'
            }, {
                closer: true,
                content: 'Да',
                onClick: _ => this.RemoveUser(user, physically)
            }]
        });
    }

    ResetUserNewDeviceInfo(userLid) {
        delete this.devicesInLoad[userLid];
    }

    GetUsersDevicesLoadingState(userLid) {
        if (this.devicesInLoad[userLid] == null) {
            this.devicesInLoad[userLid] = {
                devices: {},
                newDevice: {
                    adding: false,
                    os: '',
                    code: ''
                }
            };
        }

        return this.devicesInLoad[userLid];
    }

    GetDeviceLoadingState(userLid, deviceCode) {
        if (this.GetUsersDevicesLoadingState(userLid).devices[deviceCode] == null) {
            this.GetUsersDevicesLoadingState(userLid).devices[deviceCode] = {
                doesRemoving: false,
                doesLoggingStateChanging: false,
                doesForceResetChanging: false,
                doesNotifyNewVersionChanging: false,
                doesPushServiceChanging: false
            };
        }

        return this.GetUsersDevicesLoadingState(userLid).devices[deviceCode];
    }

    /**
     * @param {WebUser} user
     * @param {string} code
     * @param {string} type
     */
    AddDevice(user, code, type) {
        UsersRequests.AddDevice(user.id, code, type)
            .then((addDeviceInfo) => {
                user = this.tableStore.data.find(x => x.id === user.id);

                this.ResetUserNewDeviceInfo(user.lid);

                if (!user)
                    return;

                if (!addDeviceInfo.error) {
                    if (user.devices.some(d => d.code == addDeviceInfo.device.code) == false)
                        user.devices.push(addDeviceInfo.device);
                    ShowAlert('success', 'Устройство успешно добавлено.');
                } else {
                    ShowAlert('error',
                        <div>
                            {addDeviceInfo.error.errorText}
                        </div>
                    );
                }
            });
    }

    /**
     * @param {WebUser} user 
     * @param {boolean} physically
     */
    RemoveUser(user, physically) {
        if (physically)
            UsersRequests.DeleteUserPhysically(user.id).then(resp => this.HandleRemoveUser(resp, user, physically));
        else
            UsersRequests.DeleteUser(user.id).then(resp => this.HandleRemoveUser(resp, user, physically));
    }

    /**
     * @param {DeleteUserResponse} resp
     * @param {WebUser} user 
     * @param {boolean} physically
     */
    HandleRemoveUser(resp, user, physically) {
        if (resp.error)
            return;

        let userState = this.usersState[user.lid];
        let userLineInfo = this.usersLinesInfos.Get(user.lid);

        userState.task = physically
            ? SynchronizingTask.removingUserPhysically
            : SynchronizingTask.removingUser;
        userState.status = SynchronizingStatus.inProgress;
        userState.inProgress = true;

        userLineInfo.closed = true;
    }

    async ShareCode(deviceCode) {
        let result = await UsersRequests.GetLoginDeepLink(deviceCode);

        if (result.error) {
            switch (result.error.errorCode) {
                case WebErrorCodeEnum.ExternalSmrUrlSettingEmptyError:
                    ShowAlert('error', 'Невозможно сгенерировать DeepLink. Пожалуйста, введите "Внешний адрес СМР" в разделе "Настройки".')
                    break;
            }

            return;
        }

        if (navigator.clipboard == undefined) {
            ShowAlert('error', 'DeepLink не скопирован. Буфер обмена недоступен.');
            return;
        }

        navigator.clipboard.writeText(result.deepLink).then(
            () => {
                ShowAlert('success', 'DeepLink успешно скопирован.');
            },
            () => {
                ShowAlert('error', 'DeepLink не скопирован. Буфер обмена недоступен.');
            });
    }

    /**
     * @param {WebUser} user
     * @param {WebDevice} device
     */
    RemoveDevice(user, device) {
        let userLid = user.lid;
        let deviceCode = device.code;

        let remove = () => {
            this.GetDeviceLoadingState(userLid, deviceCode).doesRemoving = true;

            UsersRequests.DeleteDevice(deviceCode)
                .then(action((result) => {
                    if (result.error)
                        return;

                    user = this.tableStore.data.find(u => u.lid == userLid);
                    if (!user)
                        return;

                    var index = user.devices.findIndex(d => d.code == deviceCode);
                    if (index >= 0)
                        user.devices.splice(index, 1);
                }))
                .finally(action(() => this.GetDeviceLoadingState(userLid, deviceCode).doesRemoving = false));
        };

        if (device.lastLoginDate)
            ShowModal({
                canBeClosed: true,
                header: `Удаление кода устройства`,
                width: '480px',
                children: (
                    <div>
                        Код устройства используется в мобильном приложении пользователя. Если удалить его сейчас:
                        <ul>
                            <li>Приложение не синхронизируется с сервером.</li>
                            <li>Для восстановления работы потребуется создать<br />и ввести новый код на устройстве.</li>
                        </ul>
                        Продолжить?
                    </div>),
                buttons: [
                    {
                        closer: true,
                        content: 'Отменить'
                    },
                    {
                        closer: true,
                        onClick: remove,
                        className: 'blue-button',
                        content: 'Продолжить'
                    }]
            });
        else
            remove();
    }

    ChangeDeviceSetting = action((userLid, deviceCode, newState, fieldNameDeviceLoadingStateChanging, fieldNameDeviceSetting, backendFuncName) => {
        this.GetDeviceLoadingState(userLid, deviceCode)[fieldNameDeviceLoadingStateChanging] = true;

        UsersRequests.ChangeDeviceSetting(backendFuncName, deviceCode, newState)
            .then(action((changedDeviceInfo) => {
                let user = this.tableStore.data.find(i => i.lid == userLid);
                if (user == null)
                    return;

                let device = user.devices.find(it => it.code == changedDeviceInfo.device.code);
                if (device == null)
                    return;

                device[fieldNameDeviceSetting] = changedDeviceInfo.device[fieldNameDeviceSetting];
            }))
            .finally(action(() => this.GetDeviceLoadingState(userLid, deviceCode)[fieldNameDeviceLoadingStateChanging] = false));
    });

    ChangeTraceKeys(deviceCode, newState, userLid) {
        this.ChangeDeviceSetting(userLid, deviceCode, newState, "doesLoggingStateChanging", "traceKeys", "ChangeTraceKeys");
    }

    ChangeNotifyNewVersionState(deviceCode, newState, userLid) {
        this.ChangeDeviceSetting(userLid, deviceCode, newState, "doesNotifyNewVersionChanging", "notifyNewVersion", "ChangeNotifyNewVersion");
    }

    ChangeForceResetDevice(deviceCode, newState, userLid) {
        this.ChangeDeviceSetting(userLid, deviceCode, newState, "doesForceResetChanging", "forceReset", "ChangeForceReset");
    }

    ChangePushService(deviceCode, newState, userLid) {
        this.ChangeDeviceSetting(userLid, deviceCode, newState, "doesPushServiceChanging", "pushProvider", "ChangePushProvider");
    }

    RequestClientDbSetStatusPending(deviceCode, userLid) {
        this.ChangeDeviceSetting(userLid, deviceCode, null, "doesDbRequestStatusChanging", "dbRequestStatus", "RequestClientDbSetStatusPending");
    }

    UpdateUsersFromTimer() {
        this.UpdateUsers(this.lastFilterString, this.pageToSet, true);
    }

    GetUsers(filterString, pageToSet) {
        this.UpdateUsers(filterString, pageToSet, false);
    }

    UpdateUsers = action((filterString, pageToSet, isUpdateFromTimer) => {
        if (this.tableStore == null)
            return;

        if (this.usersRequested === true && isUpdateFromTimer == true)
            return;

        this.usersRequestId = this.usersRequestId + 1;
        if (pageToSet)
            this.pageToSet = pageToSet;
        if (!this.pageToSet)
            this.pageToSet = 1;

        this.lastFilterString = filterString;

        let { elementsPerPage } = this.tableStore.paginationInfo;
        let start = (this.pageToSet - 1) * elementsPerPage;

        if (this.tableStore.data.length == 0 && isUpdateFromTimer == false) {
            this.usersLoading = true;
        }
        var skipError = isUpdateFromTimer;
        this.usersRequested = true;
        let currentRequestId = this.usersRequestId;
        UsersRequests.GetUsers(
            start,
            elementsPerPage,
            this.lastFilterString,
            isUpdateFromTimer,
            skipError
        )
            .then(action((values) => {
                if (this.tableStore == null)
                    return;

                // Защита от наслаивающихся запросов,
                // чтобы обработался только последний запроса.
                if (currentRequestId !== this.usersRequestId)
                    return

                if (!this.tableStore.HandlePagination(this.SetPageData, values.countTotal, this.pageToSet, page => this.GetUsers(this.lastFilterString, page))) {
                    return;
                }

                this.HandleDataUpdateUsersStoreAndProcessUserState(values);
            }))
            .finally(action(() => {
                this.usersLoading = false;
                if (currentRequestId === this.usersRequestId)
                    this.usersRequested = false;
            }));
    });

    SetPageData(pageToSet) {
        Helper.PushOrUpdateHistory('/users', { page: pageToSet });

        AppStore.SetLastPageInfo({
            pageTitle: 'users',
            params: {
                page: `${pageToSet}`
            }
        });
    }

    /** @param {GetUsersResponse} data  */
    HandleDataUpdateUsersStoreAndProcessUserState(data) {
        /** @type {Object.<string, UserState>} */
        let newUsersState = {};

        data.webUsersForPageOrAll.forEach(userFromServer => {
            newUsersState[userFromServer.lid] = userFromServer.state;
        });

        let newPageUsers = data.webUsersForPageOrAll.filter(u => this.tableStore.data.some(oldU => oldU.id === u.id) === false);
        this.tableStore.data = data.webUsersForPageOrAll;

        newPageUsers.forEach(u => {
            let lineInfo = this.usersLinesInfos.Get(u.lid);
            lineInfo.closed = true;
        });

        this.SetUsersState(newUsersState, null);

        if (Helper.ArrayCompare(this.deviceDbDumps, data.deviceDbDumps, (it) => it.id) == false)
            this.deviceDbDumps = data.deviceDbDumps;
    }

    DropUserReloadState() {
        this.isShowedUserSyncProcessModal = false;
    }

    /**
     * @param {Object.<string, UserState>} usersState 
     * @param {UsersReloadState} usersReloadState 
     * @returns 
     */
    SetUsersState(usersState, usersReloadState) {
        if (this.tableStore == null)
            return;

        let statePairs = Object.keys(usersState).map(lid => {
            let oldState = this.usersState[lid];
            this.usersState[lid] = usersState[lid];
            return { userLid: lid, oldState: oldState, newState: usersState[lid] };
        });
        this.ProcessUsersStates(statePairs);

        if (usersReloadState != null) {
            this.usersReloadState = usersReloadState;
            if (this.usersReloadState?.currentProccessedUserLid != null)
                this.usersReloadStateCurrentProcessedUserState = usersState[usersReloadState.currentProccessedUserLid];

            this.SetUsersReloadState(usersReloadState);
        }
    }

    /** @param {UsersReloadState} usersReloadState  */
    SetUsersReloadState(usersReloadState) {
        this.usersSyncTotalDocumentsCount = usersReloadState.progress;

        if (usersReloadState.inProcess && !this.isShowedUserSyncProcessModal) {
            if (Object.keys(this.listOfUsersToBeLoaded).length === 0) {
                this.GetInitData();
            }
            else {
                this.showSyncSyncedProcessModal();
                this.isShowedUserSyncProcessModal = true;
            }
        } else if (!usersReloadState.inProcess && this.isShowedUserSyncProcessModal) {

            this.isShowedUserSyncProcessModal = false;

            this.closeSyncSyncedProcessModal();

            if (this.usersReloadState.isCancelled) {
                ShowAlert('warning', 'Синхронизация успешно завершена для ' + this.usersReloadState.usersLoadedCount + ' из ' + this.usersReloadState.usersQueueInitialCount + ' пользователей');
            }

            UsersRequests.GetResyncUsersResult().then(values => {
                this.usersReloadResult = values;
                this.showSyncSyncedResultModal();
            });
        }
    }

    /** @param {{userLid: string, oldState: UserState, newState: UserState}[]} statePairs */
    ProcessUsersStates(statePairs) {
        statePairs.forEach(({ userLid, oldState, newState }) => {
            let userLineInfo = this.usersLinesInfos.Get(userLid);

            if (oldState?.inProgress == true &&
                newState.inProgress == false &&
                newState.task === SynchronizingTask.removingUser) {
                ShowAlert('success', `Пользователь ${newState.userName} логически удален.`);
            }

            if (oldState?.inProgress == true &&
                newState.inProgress == false &&
                newState.task === SynchronizingTask.removingUserPhysically) {
                ShowAlert('success', `Пользователь ${newState.userName} физически удален.`);
            }

            if (oldState?.inProgress == true &&
                newState.inProgress == false &&
                newState.task === SynchronizingTask.removingUserDl) {
                ShowAlert('success', `ДЛ пользователя ${newState.userName} удалено.`);
            }

            if ((newState.task === SynchronizingTask.removingUser ||
                newState.task === SynchronizingTask.removingUserDl) &&
                newState.inProgress === true) {
                userLineInfo.closed = true;
            }

            let line = document.getElementById(`user-line-${userLid}`);
            if (line != null) {
                let parent = line.parentElement;
                userLineInfo.topPopup = line.offsetTop - parent.offsetTop - parent.scrollTop + line.offsetHeight + 30 - parent.offsetHeight > 0;
            }
        });
    }

    ClearResyncUsersResult() {
        UsersRequests.ClearResyncUsersResult();
    }

    SelectUsersToSyncModalLoadUsersCanSync() {
        UsersRequests.GetBulkSyncSettings()
            .then((values) => {
                var usersNamesByLids = {};
                values.webUsersCanSync.forEach(it => {
                    usersNamesByLids[it.lid] = it.userName;
                });
                this.selectSyncUsersModalData.usersNamesByLids = usersNamesByLids;

                this.selectSyncUsersModalData.receiveDocumentsFromIsNeeded = values.receiveDocumentsFromIsNeeded;
            });
    }

    SyncSyncedUsers() {
        SettingsRequests.SyncSyncedUsers({ allProductModes: false })
            .then(action((values) => {
                if (!values.error) {
                    ShowAlert('success', "Успешно запущена синхронизация активированных пользователей");
                } else {
                    ShowAlert('error', 'Не удалось запустить синхронизацию пользователей. Перейдите в раздел "Протоколирование"');
                }
            }));
    }

    SelectUsersToSyncModalSyncSelectedUsersClick() {
        if (this.IsCanSync() == false)
            return;

        UsersRequests.SyncUsers(this.selectSyncUsersModalData.selectedUsersLids, this.selectSyncUsersModalData.receiveDocumentsFrom);
    }

    IsCanSync() {
        var ret = IntegrationSettingsStore.dmsis.hasError == false;
        if (ret == false) {
            ShowAlert('error', 'Сервис интеграции недоступен');
        }
        return ret;
    }

    /** @param {string} deviceCode  */
    GetDeviceDbDumps(deviceCode) {
        return this.deviceDbDumps.filter(x => x.deviceCode == deviceCode);
    }

    ShowDeviceDbModal(deviceCode) {
        ShowModal({
            canBeClosed: true,
            header: 'Выгрузка базы данных',
            children: <DeviceDbModal deviceCode={deviceCode} />,
            bodyStyle: { padding: '0px' },
            width: '800px'
        })
    }

    /** @param {DeviceDataDumpWeb} dumpInfo  */
    DeleteDeviceDataDump(dumpInfo) {
        UsersRequests.DeleteDeviceDataDump(dumpInfo)
            .then(() => {
                let deviceCode = dumpInfo.deviceCode;
                let fileName = dumpInfo.fileNameWithExtension;
                this.deviceDbDumps = this.deviceDbDumps.filter(it => it.fileNameWithExtension != fileName);
                let currentDeviceCodeDeviceDbDumps = this.GetDeviceDbDumps(deviceCode);

                TracesStore.AddTrace("UsersStore", "DeleteDeviceDataDump", "", {
                    deviceCode: deviceCode,
                    fileName: fileName,
                    newDeviceDbDumps: this.deviceDbDumps,
                    currentDeviceCodeDeviceDbDumps: currentDeviceCodeDeviceDbDumps
                });

                if (currentDeviceCodeDeviceDbDumps.length > 0) {
                    this.ShowDeviceDbModal(deviceCode);
                }
                else {
                    CloseModal();
                }
            });
    }

    SendTestPushAlert(deviceCode) {
        this.testPushAlertSending = true;
        UsersRequests.SendTestPushAlert(deviceCode)
            .then(action((values) => {
                this.testPushAlertSending = false;
                if (values?.error) {
                    ShowAlert('error', `Произошла ошибка при отправке тестового уведомления: ${values.error}`);
                }
                else {
                    ShowAlert('success', 'Тестовое уведомление успешно отправлено.');
                }
            }));
    }

    ExportClientLogs(folderName) {
        SettingsRequests.ExportLogsTaskRun({ folderName: folderName }).then(action((result) => {
            if (result.error)
                return;

            TasksStore.SetCurrentDownloadingTask(result.value, {
                canBeClosed: false,
                header: `Архивация пользовательских логов`,
                getChildren: () => (<div>
                    <div className="document-history-progress-modal-description">
                        Выполняется формирование архива. Пожалуйста, подождите...
                    </div>
                    <LoadingIndicator loaded={TasksStore.currentDownloadingTask?.progress}
                        total={100}
                        runner={false} />
                </div>),
                getButtons: () =>
                    [
                        {
                            content: 'Отменить',
                            closer: true,
                            onClick: () => TasksStore.CancelCurrentDownloadingTask(),
                            className: TasksStore.currentDownloadingTask?.isCanceled ? "disabled" : null
                        }
                    ]
            });
        }));
    }
}

export default new UsersStore();